// Results from LatchBio's benchmarks.bio V1 capabilities leaderboard (complete-coverage
// view). Scores are macro-mean pass rates in percent. Regenerate from
// https://benchmarks.bio/results/v1/latest.json when LatchBio publishes an update.

export type BenchmarkId = 'overall' | 'txbench' | 'epibench' | 'variantbench';

export interface Config {
  model: string;
  harness: string;
  provider: string;
  /** Weighted mean over the capability benchmarks the config completed. */
  coverage: string;
  scores: Record<BenchmarkId, number | null>;
}

export interface Benchmark {
  id: BenchmarkId;
  name: string;
  /** Plain-English description of what the score measures. */
  blurb: string;
}

export const DATA_AS_OF = '2026-09-29';
export const SOURCE = {
  label: 'benchmarks.bio',
  href: 'https://benchmarks.bio/results/v1/'
};

export const BENCHMARKS: Benchmark[] = [
  {
    id: 'overall',
    name: 'Overall',
    blurb:
      'Average across all 12 capability benchmarks in the leaderboard, weighted by number of tasks.'
  },
  {
    id: 'txbench',
    name: 'TxBench-PP',
    blurb:
      'Small-molecule preclinical pharmacology: can the agent make the right drug-program calls? 100 tasks.'
  },
  {
    id: 'epibench',
    name: 'EpiBench',
    blurb:
      'Epigenomics: ChIP-seq, ATAC-seq, CUT&Tag and methylation analysis from real datasets. 106 tasks.'
  },
  {
    id: 'variantbench',
    name: 'VariantBench',
    blurb:
      'Genetics: find and interpret disease-relevant variants from sequencing data. 118 tasks.'
  }
];

export const PROVIDER_COLORS: Record<string, string> = {
  Anthropic: '#E55B3F',
  OpenAI: '#10a37f',
  Google: '#4285F4',
  SpaceXAI: '#71717a'
};

const c = (
  model: string,
  harness: string,
  provider: string,
  overall: number,
  coverage: string,
  txbench: number | null,
  epibench: number | null,
  variantbench: number | null
): Config => ({
  model,
  harness,
  provider,
  coverage,
  scores: { overall, txbench, epibench, variantbench }
});

export const CONFIGS: Config[] = [
  c('GPT-6 Astra', 'Pi', 'OpenAI', 51.0, '12/12', 59.3, 28.3, 46.6),
  c('Claude Opus 5', 'Pi', 'Anthropic', 49.6, '12/12', 63.0, 25.2, 50.6),
  c(
    'Claude Opus 5',
    'Claude Code',
    'Anthropic',
    49.0,
    '12/12',
    59.7,
    20.8,
    52.8
  ),
  c('GPT-6 Astra', 'Codex', 'OpenAI', 48.9, '12/12', 59.3, 24.2, 45.2),
  c('Grok 4.6', 'Pi', 'SpaceXAI', 47.0, '12/12', 61.7, 23.6, 40.4),
  c('Grok 4.7', 'Grok Build', 'SpaceXAI', 47.0, '12/12', 54.7, 20.8, 45.5),
  c('GPT-6 Sol', 'Pi', 'OpenAI', 46.4, '12/12', 63.0, 22.0, 38.7),
  c('Grok 4.6', 'Grok Build', 'SpaceXAI', 45.6, '12/12', 57.0, 21.1, 39.0),
  c('Claude Opus 4.8', 'Pi', 'Anthropic', 45.5, '12/12', 57.7, 25.8, 42.4),
  c(
    'Claude Opus 4.8',
    'Claude Code',
    'Anthropic',
    43.7,
    '12/12',
    54.3,
    20.4,
    42.4
  ),
  c('GPT-5.6 Sol', 'Pi', 'OpenAI', 43.1, '12/12', 61.3, 21.4, 35.3),
  c('Claude Opus 5.5', 'Pi', 'Anthropic', 42.4, '12/12', 26.3, 39.0, 50.6),
  c('GPT-5.6 Sol', 'Codex', 'OpenAI', 42.1, '11/12', null, 25.8, 34.2),
  c('GPT-5.5', 'Pi', 'OpenAI', 42.1, '11/12', 55.3, 28.3, 30.5),
  c(
    'Claude Opus 5.5',
    'Claude Code',
    'Anthropic',
    42.0,
    '12/12',
    30.7,
    38.7,
    49.7
  ),
  c('Claude Sonnet 5', 'Pi', 'Anthropic', 41.0, '12/12', 49.3, 19.5, 39.8),
  c('Gemini 3.7 Flash', 'Pi', 'Google', 40.4, '12/12', 55.3, 22.0, 31.4),
  c(
    'Claude Sonnet 5',
    'Claude Code',
    'Anthropic',
    40.1,
    '12/12',
    49.3,
    21.7,
    39.0
  ),
  c('Claude Opus 4.7', 'Pi', 'Anthropic', 38.2, '12/12', 45.3, 22.6, 35.0),
  c(
    'Claude Opus 4.7',
    'Claude Code',
    'Anthropic',
    38.1,
    '11/12',
    42.3,
    18.9,
    33.1
  ),
  c('Gemini 3.8 Flash', 'Pi', 'Google', 37.5, '12/12', 54.7, 21.4, 31.4),
  c('GPT-6 Luna', 'Pi', 'OpenAI', 37.2, '12/12', 44.3, 19.2, 27.7),
  c('GPT-5.6 Luna', 'Codex', 'OpenAI', 33.9, '12/12', 43.7, 19.8, 27.1),
  c('GPT-5.6 Luna', 'Pi', 'OpenAI', 33.3, '12/12', 43.3, 18.9, 24.0),
  c('Claude Sonnet 4.6', 'Pi', 'Anthropic', 31.6, '12/12', 34.3, 18.9, 31.1),
  c(
    'Claude Sonnet 4.6',
    'Claude Code',
    'Anthropic',
    31.2,
    '12/12',
    33.3,
    17.3,
    26.3
  )
];
